import { Box, Container } from '@mui/material';
import Link from 'next/link';
import * as React from 'react';
import { getLocalizedPromoBannerUrl, sanitizePromoBannerUrl } from 'helpers/promoBanner';
import { theme } from 'styles/mui-theme';
import { useLocale } from './LocalizationProvider';

export type PromoBannerConfig = {
    text: string;
    textFr: string;
    linkUrl: string;
    linkUrlFr: string;
    linkText: string;
    linkTextFr: string;
    startDate: string | null;
    endDate: string | null;
};
const LINK_PLACEHOLDER = '{link}';

/**
 * Renders banner text with a link placeholder.
 * Use {link} in the text to place the link, e.g., "Check out {link} today!"
 * If no placeholder is found, the link is prepended to the text.
 */
function renderBannerContent(text: string, linkText: string, linkUrl: string): React.ReactNode {
    const safeLinkUrl = sanitizePromoBannerUrl(linkUrl);

    if (!text.includes(LINK_PLACEHOLDER)) {
        // Fallback: prepend link to text (backwards compatible)
        return (
            <Link href={safeLinkUrl} target="_blank" rel="noopener noreferrer">
                <span css={{ textDecoration: 'underline' }}>{linkText}</span> {text}
            </Link>
        );
    }

    const parts = text.split(LINK_PLACEHOLDER);

    return (
        <>
            {parts[0]}
            <Link href={safeLinkUrl} target="_blank" rel="noopener noreferrer" css={{ textDecoration: 'underline' }}>
                {linkText}
            </Link>
            {parts.slice(1).join(LINK_PLACEHOLDER)}
        </>
    );
}

export const PromoBanner = ({ config }: { config: PromoBannerConfig | null }) => {
    const locale = useLocale();

    if (!config) {
        return null;
    }

    const isFrench = locale === 'fr-CA';
    const text = isFrench && config.textFr ? config.textFr : config.text;
    const linkText = isFrench && config.linkTextFr ? config.linkTextFr : config.linkText;
    const linkUrl = getLocalizedPromoBannerUrl(locale, config.linkUrl, config.linkUrlFr);
    return (
        <Box
            sx={{
                backgroundColor: theme.tritonColors.darkBlue,
                color: theme.tritonColors.veryLightBlue,
                py: 1,
            }}
        >
            <Container
                css={{
                    textAlign: 'center',
                }}
            >
                {renderBannerContent(text, linkText, linkUrl)}
            </Container>
        </Box>
    );
};

/**
 * Preview version of the banner for the admin page.
 */
export const PromoBannerPreview = ({
    text,
    linkText,
    linkUrl,
}: {
    text: string;
    linkText: string;
    linkUrl: string;
}) => {
    if (!text && !linkText) {
        return (
            <Box
                sx={{
                    backgroundColor: theme.tritonColors.darkBlue,
                    color: theme.tritonColors.veryLightBlue,
                    py: 1,
                    textAlign: 'center',
                }}
            >
                <em>Enter text to see preview</em>
            </Box>
        );
    }

    const displayText = text || 'Banner text with {link} placeholder';
    const displayLinkText = linkText || 'Link text';
    const displayLinkUrl = linkUrl || '#';

    return (
        <Box
            sx={{
                backgroundColor: theme.tritonColors.darkBlue,
                color: theme.tritonColors.veryLightBlue,
                py: 1,
            }}
        >
            <Container
                css={{
                    textAlign: 'center',
                }}
            >
                {renderBannerContent(displayText, displayLinkText, displayLinkUrl)}
            </Container>
        </Box>
    );
};
